import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import Uppy, { Meta } from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import StatusBar from '@uppy/status-bar';
import XHR from '@uppy/xhr-upload';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { FileUploadDomHelper } from './file-upload-dom.helper';
import { FileUploadErrorHandler } from './file-upload-error.handler';
import { FileUploadOptionsProvider } from './file-upload-options.provider';
import { FileUploadHelper } from './file-upload.helper';
import { FileUploadResponseModel } from '../../models/interfaces/file-upload-response.model';
import { FileUploadOptionsModel } from '../../models/interfaces/file-upload.model';
import { RetryService } from '../retry.service';

@Injectable({
    providedIn: 'root'
})
export class FileUploadService {
    private readonly PRIVATE_LINK_ENDPOINT = '/file-management-api/url';
    private readonly MAX_FILE_SIZE = 10485760;

    private onDashboardModalCloseSubject = new Subject<void>();
    private response: FileUploadResponseModel[] = [];
    private uppyInstances: Uppy<Meta, Record<string, never>>[] = [];

    private translateService = inject(TranslateService);
    private optionsProvider = new FileUploadOptionsProvider(new FileUploadDomHelper(this.translateService));
    private domHelper = new FileUploadDomHelper(this.translateService);
    private helper = new FileUploadHelper();
    private errorHandler = new FileUploadErrorHandler(this.translateService);

    constructor(
        private retryService: RetryService,
        private httpClient: HttpClient
    ) {}

    async init(
        options: FileUploadOptionsModel,
        onComplete?: (...args: any[]) => void
    ): Promise<Uppy<Meta, Record<string, never>>> {
        if (this.hasDashboardTrigger(options)) {
            return await this.retryInitializeWithTrigger(options, onComplete);
        }
        return await this.initializeUppy(options, onComplete);
    }

    getDefaultOptions(
        target?: string,
        trigger?: string | null,
        lang: string = 'en'
    ): FileUploadOptionsModel {
        return this.optionsProvider.getDefaultOptions(target, trigger, lang);
    }

    onDashboardModalClose(): Observable<void> {
        return this.onDashboardModalCloseSubject.asObservable();
    }

    private hasDashboardTrigger(options: FileUploadOptionsModel): boolean {
        return !!options?.dashboard?.trigger;
    }

    private async retryInitializeWithTrigger(
        options: FileUploadOptionsModel,
        onComplete?: (...args: any[]) => void
    ): Promise<Uppy<Meta, Record<string, never>>> {
        return await this.retryService.retry(
            () => document.querySelector(options.dashboard?.trigger),
            async() => await this.initializeUppy(options, onComplete),
            // eslint-disable-next-line no-console
            () => console.error('Failed to find element with selector')
        );
    }

    private async initializeUppy(
        options: FileUploadOptionsModel,
        onComplete?: (...args: any[]) => void
    ): Promise<Uppy<Meta, Record<string, never>>> {
        options = this.helper.setFileSizeNoteIfCustom(options, this.MAX_FILE_SIZE);

        const uppy = this.setupUppyInstance(options);
        const configuredUppy = this.registerUppyEvents(uppy, options, onComplete);

        return Promise.resolve(configuredUppy);
    }

    private setupUppyInstance(options: FileUploadOptionsModel): Uppy<Meta, Record<string, never>> {
        const uppy = this.getUppyInstanceByTargetId(options.dashboard?.target);
        uppy?.destroy();
        return new Uppy(options.general)
            .use(Dashboard, options.dashboard)
            .use(StatusBar)
            .use(XHR, options.xhr);
    }

    private registerUppyEvents(
        uppy: Uppy<Meta, Record<string, never>>,
        options: FileUploadOptionsModel,
        onComplete?: (...args: any[]) => void
    ): Uppy<Meta, Record<string, never>> {
        uppy.on('file-added', (file: any) => this.onFileAdded(file, options, uppy));
        uppy.on('upload-success', (_file, response) => this.onUploadSuccess(response));
        uppy.on('upload-error', (file, _error, response) => this.onUploadError(file, response));
        uppy.on('complete', () => this.onUploadComplete(uppy, onComplete));
        uppy.on('dashboard:modal-closed', () => this.triggerOnDashboardModalClose(options, uppy));
        uppy = this.applyDomOverrides(uppy, options);
        this.uppyInstances.push(uppy);

        return uppy;
    }

    private onFileAdded(file: any, options: FileUploadOptionsModel, uppy: Uppy): void {
        const metadata = this.helper.createFileMetadata(file.extension, options);
        uppy.setFileMeta(file.id, { ...metadata });
    }

    private onUploadSuccess(response: any): void {
        if (!response.body) {
            return;
        }

        const successFile = this.helper.createSuccessResponse(response.body);
        this.response.push(successFile);
    }

    private onUploadError(file: any, response: any): void {
        const errorDetails = this.errorHandler.parseErrorResponse(response);
        const failedFile = this.helper.createFailedResponse(
            file.name,
            errorDetails.message,
            errorDetails.code
        );

        this.response.push(failedFile);
    }

    private onUploadComplete(uppy: Uppy, callback?: (...args: any[]) => void): void {
        if (!callback) {
            this.clearResponse();
            return;
        }

        if (this.helper.hasPrivateFiles(this.response)) {
            this.handlePrivateFiles(callback);
        } else {
            callback(this.response);
        }

        this.showUploadMessages(uppy);
        this.clearResponse();
    }

    private handlePrivateFiles(callback: (...args: any[]) => void): void {
        this.generatePrivateLinks(this.response).subscribe((privateFiles: FileUploadResponseModel[]) => {
            callback(privateFiles);
        });
    }

    private generatePrivateLinks(files: FileUploadResponseModel[]): Observable<FileUploadResponseModel[]> {
        const updateCalls = files.map((file) => this.processFileLink(file));
        return forkJoin(updateCalls);
    }

    private processFileLink(file: FileUploadResponseModel): Observable<FileUploadResponseModel> {
        return file.public
            ? of(file)
            : this.getPrivateLink(file.id).pipe(
                switchMap((response) => this.attachPrivateLink(file, response?.url)),
                catchError((error) => of(this.errorHandler.handleLinkError(error)))
            );
    }

    private attachPrivateLink(
        file: FileUploadResponseModel,
        url: string
    ): Observable<FileUploadResponseModel> {
        file.directLink = url;
        return of(file);
    }

    private getPrivateLink(fileId: string): Observable<any> {
        return this.httpClient.get(`${this.PRIVATE_LINK_ENDPOINT}/${fileId}`);
    }

    private showUploadMessages(uppy: Uppy<Meta, Record<string, never>>): void {
        this.response.forEach((file) => {
            const message = this.errorHandler.createUploadMessage(file);
            const messageType = this.helper.isFailedStatus(file.status) ? 'error' : 'success';
            uppy?.info(message, messageType, 3000);
        });
    }

    private clearResponse(): void {
        this.response = [];
    }

    private triggerOnDashboardModalClose(options: FileUploadOptionsModel, uppy: Uppy<Meta, Record<string, never>>): void {
        this.domHelper.cleanupTargetElement(options?.dashboard?.target);
        this.onDashboardModalCloseSubject.next();
        uppy.clear();
    }

    private applyDomOverrides(
        uppy: Uppy<Meta, Record<string, never>>,
        options: FileUploadOptionsModel
    ): Uppy<Meta, Record<string, never>> {
        const originalEmit = uppy.emit.bind(uppy);

        uppy.emit = (event, ...args) => {
            setTimeout(() => {
                this.domHelper.assignAttributes(options);
                this.domHelper.injectCustomElements(options);
            }, 0);

            return originalEmit(event, ...args);
        };

        return uppy;
    }

    private getUppyInstanceByTargetId(targetId: string): Uppy<Meta, Record<string, never>> | undefined {
        return this.uppyInstances.find(((uppy) => {
            const dashboard = uppy.getPlugin('Dashboard');
            // Check if the dashboard plugin's target matches the targetId
            return dashboard?.opts?.target === targetId;
        }));
    }

    openModal(uppy: Uppy<Meta, Record<string, never>>): void {
        const dashboard: { openModal(): void; } | any = uppy.getPlugin('Dashboard');
        dashboard?.openModal();
    }

    closeModal(uppy: Uppy<Meta, Record<string, never>>): void {
        const dashboard: { closeModal(): void; } | any = uppy.getPlugin('Dashboard');
        dashboard?.closeModal();
    }
}
